import { Controller } from '@hotwired/stimulus'
import * as ActiveStorage from '@rails/activestorage'

// Reusable Active Storage Direct Upload for ANY form field. A chosen file is
// uploaded straight to object storage (the bytes never touch the app server),
// upload progress is reported, the returned blob signed_id is written into a
// hidden input, and — by default — the form is submitted. Size/type are checked
// client-side for instant feedback; the server must still re-validate the real
// bytes. The progress/preview/error UI is all optional (guarded by hasXTarget),
// so a consumer wires up only what it needs.
//
// Minimal usage (just upload + submit):
//   <form data-controller="direct-upload"
//         data-direct-upload-url-value="<%= rails_direct_uploads_path %>">
//     <input type="hidden" name="record[file]" data-direct-upload-target="signedId">
//     <input type="file" data-direct-upload-target="input" data-action="direct-upload#select">
//   </form>
//
// Optional values: max-bytes, accept (JSON array of MIME types), auto-submit
// (default true), and the too-large / bad-type / error message strings.
// Optional targets: progress, progressBar, progressText, error, preview.
export default class extends Controller {
  static values = {
    url: String,
    maxBytes: { type: Number, default: 0 },
    accept: { type: Array, default: [] },
    autoSubmit: { type: Boolean, default: true },
    tooLarge: { type: String, default: '' },
    badType: { type: String, default: '' },
    error: { type: String, default: 'That upload did not go through. Please try again.' }
  }

  static targets = ['input', 'signedId', 'progress', 'progressBar', 'progressText', 'error', 'preview']

  select() {
    const file = this.inputTarget.files[0]
    if (!file) return
    this.hideError()

    if (this.acceptValue.length && !this.acceptValue.includes(file.type)) {
      return this.reject(this.badTypeValue)
    }
    if (this.maxBytesValue && file.size > this.maxBytesValue) {
      return this.reject(this.tooLargeValue)
    }

    this.showPreview(file)
    this.start()

    // Passing `this` as the delegate lets us hook the upload XHR for progress.
    const upload = new ActiveStorage.DirectUpload(file, this.urlValue, this)
    upload.create((error, blob) => {
      if (error) {
        this.finish()
        return this.fail(this.errorValue)
      }
      this.signedIdTarget.value = blob.signed_id
      if (this.autoSubmitValue) {
        this.element.requestSubmit()
      } else {
        this.finish()
      }
    })
  }

  // Active Storage DirectUpload delegate hook: wire the storage PUT's progress
  // events to the progress bar.
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        this.setProgress(Math.round((event.loaded / event.total) * 100))
      }
    })
  }

  start() {
    this.inputTarget.disabled = true
    this.setProgress(0)
    if (this.hasProgressTarget) this.progressTarget.classList.remove('hidden')
  }

  finish() {
    this.inputTarget.disabled = false
    if (this.hasProgressTarget) this.progressTarget.classList.add('hidden')
  }

  setProgress(percent) {
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${percent}%`
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = `${percent}%`
  }

  showPreview(file) {
    if (!this.hasPreviewTarget) return
    this.previewTarget.src = URL.createObjectURL(file)
    this.previewTarget.classList.remove('hidden')
  }

  // A failed UPLOAD (already-validated file): keep the selection so a retry is
  // one click, just surface the error.
  fail(message) {
    this.showError(message)
  }

  // A REJECTED file (bad type/size, never uploaded): clear the picker so the
  // invalid file is not left selected, then surface why.
  reject(message) {
    this.inputTarget.value = ''
    this.showError(message)
  }

  showError(message) {
    if (!this.hasErrorTarget || !message) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden')
  }
}
